import { AdoptionForm } from '../components';
import { Box, Flex, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToast } from '@lon/shared/components';
import { useMediaQuery, useQueryParams } from '@lon/shared/hooks';
import {
  useRegisterAdoptionReviewerMutation,
  useRegisterCommunityAccessMutation,
} from '@lon/shared/requests';
import { utils } from './duck';
import { Welcome } from './components';

const AdoptionRegistration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast, closeAllToasts } = useToast();
  const [params] = useQueryParams();
  const { registrationToken, communityRegistrationToken, eventCode } =
    params as any;
  const [isRegistered, setRegistered] = useState(false);
  const [districtName, setDistrictName] = useState('');
  const [eventUrlCode, setEventUrlCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const { validationSchema, defaultValues } = utils.getValidation();
  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const [executeAdoptionReviewer, { loading: loadingAdoptionReviewer }] =
    useRegisterAdoptionReviewerMutation();
  const [
    executeCommunityAccessMember,
    { loading: loadingCommunityAccessMember },
  ] = useRegisterCommunityAccessMutation();

  const loading = loadingAdoptionReviewer || loadingCommunityAccessMember;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (registrationToken) {
        const result = await executeAdoptionReviewer({
          variables: {
            input: {
              username: values.username.trim(),
              plainPassword: values.password,
              registrationToken,
            },
          },
        });
        const registerAdoptionReviewer = result.data?.registerAdoptionReviewer;

        setDistrictName(registerAdoptionReviewer?.districtName || '');
        setEventUrlCode(registerAdoptionReviewer?.eventUrlCode || '');
      } else if (communityRegistrationToken) {
        const result = await executeCommunityAccessMember({
          variables: {
            input: {
              username: values.username.trim(),
              plainPassword: values.password,
              communityRegistrationToken,
            },
          },
        });
        const registerCommunityAccessMember =
          result.data?.registerCommunityAccessMember;

        setDistrictName(registerCommunityAccessMember?.districtName || '');
        setEventUrlCode(registerCommunityAccessMember?.eventUrlCode || '');
      }

      setRegistered(true);
      setUsername(values.username);
      setPassword(values.password);

      toast({
        title: t('register.success'),
        description: t('register.successDescription', {
          user: values.username,
        }),
        variant: 'success-light',
        isClosable: true,
        duration: 3000,
      });
    } catch (error: any) {
      utils.handleError({ error, toast, username: values.username, t, i18n });
    }
  });

  return (
    <FormProvider {...form}>
      <Flex
        zIndex={1}
        direction="column"
        backgroundColor="white"
        w={{ base: 'full', sm: '100%` ' }}
        maxW={{ base: 'auto', md: 'calc(100vw - 32px)', lg: '900px' }}
        color="primary.800"
        boxShadow="0px 3px 20px 15px #2B364633"
      >
        <Box borderBottomWidth="1px" borderColor="gray.50" px={8} py={6}>
          <Text variant="h4" fontSize={'xl'}>
            {t(
              !isRegistered
                ? 'register.formTitle'
                : isMobile
                ? 'register.welcomeShort'
                : 'register.welcomeTitle'
            )}
          </Text>
        </Box>
        <Box as="form" noValidate onSubmit={onSubmit}>
          {isRegistered ? (
            <Welcome
              districtName={districtName}
              eventUrlCode={eventUrlCode}
              username={username}
              password={password}
              closeAllToasts={closeAllToasts}
            />
          ) : (
            <AdoptionForm loading={loading} eventCode={eventCode} />
          )}
        </Box>
      </Flex>
    </FormProvider>
  );
};

export default AdoptionRegistration;
